import React from 'react';
import './index.less';
import { useStore } from '../../../../store';
import { useParams } from 'react-router-dom';
import people from '../../../../../../assets/sme/ics/quiz_people.png';

interface IQuizOption {
  indicator?: string;
  title: string;
  hint: string;
  isCorrect: boolean;
}

interface IQuizProps {
  title: string;
  description: string;
  question: string;
  options: IQuizOption[];
  onCompleted?: () => void;
  quizImg?: string;
}

const INDICATORS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const HelpCircleIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const XCircleIcon: React.FC = () => (
  <svg
    width="114"
    height="114"
    viewBox="0 0 114 114"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.35">
      <path
        d="M38.3232 39.5615L72.2245 73.4628"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <path
        d="M38.3145 73.4622L72.2157 39.5609"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle
        cx="56.5101"
        cy="56.5101"
        r="53.5101"
        stroke="currentColor"
        strokeWidth="6"
      />
    </g>
  </svg>
);

const CheckCircleIcon: React.FC = () => (
  <svg
    width="129"
    height="129"
    viewBox="0 0 129 129"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M118.25 59.555V64.5C118.243 76.0908 114.49 87.3689 107.55 96.6523C100.61 105.936 90.8552 112.727 79.74 116.013C68.6249 119.3 56.7453 118.905 45.8728 114.888C35.0003 110.872 25.7175 103.448 19.4089 93.7242C13.1003 84.0007 10.1039 72.4983 10.8665 60.9327C11.6291 49.367 16.11 38.3578 23.6408 29.5468C31.1715 20.7358 41.3487 14.5952 52.6545 12.0408C63.9603 9.48639 75.7889 10.6551 86.3763 15.3725M118.25 21.5L64.5 75.3038L48.375 59.1788"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M118.25 59.555V64.5C118.243 76.0908 114.49 87.3689 107.55 96.6523C100.61 105.936 90.8552 112.727 79.74 116.013C68.6249 119.3 56.7453 118.905 45.8728 114.888C35.0003 110.872 25.7175 103.448 19.4089 93.7242C13.1003 84.0007 10.1039 72.4983 10.8665 60.9327C11.6291 49.367 16.11 38.3578 23.6408 29.5468C31.1715 20.7358 41.3487 14.5952 52.6545 12.0408C63.9603 9.48639 75.7889 10.6551 86.3763 15.3725M118.25 21.5L64.5 75.3038L48.375 59.1788"
      stroke="currentColor"
      strokeOpacity="0.2"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const CornerRightUpIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="10 9 15 4 20 9" />
    <path d="M4 20h7a4 4 0 0 0 4-4V4" />
  </svg>
);

const Quiz: React.FunctionComponent<IQuizProps> = (props) => {
  const {
    title = 'Scenario Question',
    description,
    question,
    options,
    onCompleted,
    quizImg,
  } = props;

  const { courses, language } = useStore();
  const param = useParams();
  const p_course = param.course;
  const course = courses[p_course || ''];
  const { components: settings } = course;

  options?.forEach((item, index) => {
    item.indicator = INDICATORS[index];
  });

  const [state, setState] = React.useState<{
    submitted: boolean;
    selected: IQuizOption | undefined;
    selectedValue: string;
  }>({
    submitted: false,
    selected: undefined,
    selectedValue: '',
  });

  React.useEffect(() => {
    if (onCompleted && state.submitted && state.selected?.isCorrect) {
      onCompleted();
    }
  }, [state.selected?.isCorrect, state.submitted]);

  React.useEffect(() => {
    setState((prev) => ({
      ...prev,
      selected: options?.find(
        (option) => option.indicator === prev.selectedValue
      ),
    }));
  }, [language]);

  if (!p_course) {
    return <div>Loading</div>;
  }

  const onSelect = (indicator?: string) => {
    if (state.submitted || !indicator) {
      return;
    }
    setState((prev) => ({
      ...prev,
      selectedValue: indicator,
      selected: options.find((option) => option.indicator === indicator),
    }));
  };

  const submit = () => {
    if (!state.selected) {
      return;
    }
    setState((prev) => ({ ...prev, submitted: true }));
  };

  const reset = () => {
    setState({ submitted: false, selected: undefined, selectedValue: '' });
  };

  const back = () => {
    setState((prev) => ({ ...prev, submitted: false }));
  };

  const showResult = state.submitted && Boolean(state.selected);
  const isCorrect = Boolean(state.selected?.isCorrect);
  const explanationLabel = settings?.quizFooter || '解釋：';
  // const resultTitle = `${
  //   (isCorrect ? settings?.correctLabel : settings?.wrongLabel) || ''
  // }`
  //   .replace(/[!！]\s*$/, '')
  //   .concat(state.selected?.indicator ? ` ${state.selected.indicator}` : '');
  const resultTitle = `${
    (isCorrect ? settings?.correctLabel : settings?.wrongLabel) || ''
  }`;
  return (
    <div className="sme-quiz">
      <div className="sme-quiz-figure">
        <img src={quizImg || people} alt="" />
      </div>

      <div className="sme-quiz-main">
        {!showResult && (
          <div className="sme-quiz-question-view">
            <div className="sme-quiz-badge">
              <span className="sme-quiz-badge-icon">
                <HelpCircleIcon />
              </span>
              <span className="sme-quiz-badge-text">{title}</span>
            </div>

            <div className="sme-quiz-prompt">
              {description && (
                <p
                  className="sme-quiz-desc"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              {question && (
                <p
                  className="sme-quiz-ask"
                  dangerouslySetInnerHTML={{ __html: question }}
                />
              )}
            </div>

            <div className="sme-quiz-options" role="radiogroup">
              {options?.map((option, index) => {
                const checked = option.indicator === state.selectedValue;
                return (
                  <div
                    key={index}
                    role="radio"
                    aria-checked={checked}
                    tabIndex={0}
                    className={`sme-quiz-option${
                      checked ? ' is-selected' : ''
                    }`}
                    onClick={() => onSelect(option.indicator)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(option.indicator);
                      }
                    }}
                  >
                    <span className="sme-quiz-option-dot" />
                    <span className="sme-quiz-option-text">{option.title}</span>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="sme-quiz-btn sme-quiz-btn--primary"
              disabled={!state.selected}
              onClick={submit}
            >
              <span>{settings?.submitLabel}</span>
              <ArrowRightIcon />
            </button>
          </div>
        )}

        {showResult && (
          <div
            className={`sme-quiz-result-view${
              isCorrect ? ' is-correct' : ' is-wrong'
            }`}
          >
            <div className="sme-quiz-result-icon">
              {isCorrect ? <CheckCircleIcon /> : <XCircleIcon />}
            </div>
            <div className="sme-quiz-result-title">{resultTitle}</div>
            <div className="sme-quiz-result-box">
              <p className="sme-quiz-result-label">{explanationLabel}</p>
              <p
                className="sme-quiz-result-hint"
                dangerouslySetInnerHTML={{ __html: state.selected?.hint || '' }}
              />
            </div>
            {isCorrect ? (
              <button
                type="button"
                className="sme-quiz-btn sme-quiz-btn--primary"
                onClick={back}
              >
                <span>{settings?.backLabel || '返回'}</span>
                <CornerRightUpIcon />
              </button>
            ) : (
              <button
                type="button"
                className="sme-quiz-btn sme-quiz-btn--gray"
                onClick={reset}
              >
                <span>{settings?.tryLabel}</span>
                <ArrowRightIcon />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

Quiz.defaultProps = {
  onCompleted: undefined,
};

export default Quiz;
